/**
 * The archived document behind one /docuware-invoices row, opened in a window on the page.
 *
 * WHY THIS EXISTS NEXT TO THE DEEP LINK. The deep link needs the viewer's own DocuWare login
 * (and a licence); the rule for this page is that whoever may view it sees the document
 * (masterplan DocuWare dit, E4/E5), so the bytes come through our API. The deep link stays in
 * the footer as the second way, and in the error state as the way out.
 *
 * `BabDocumentViewerModal` is the model for the blob handling and is deliberately left alone.
 * What differs: this window gets ONE row (no document tabs, no match confidence), names its
 * sections by number only, and must stay useful when a section is not a PDF.
 *
 * THINGS IT REFUSES TO PRETEND:
 *
 *  1. **A section is not "the invoice".** A DocuWare document is a staple of sections and their
 *     order carries no meaning, so the tabs are numbered, never named.
 *  2. **A failed section is not a failed document.** dit files mix PDFs and XML invoices in one
 *     document (`DWEXTENSION` ".", e.g. [xml, pdf]); section 1 can be the XML and then the
 *     endpoint answers 415. The window keeps the section bar on screen, shows the endpoint's
 *     sentence and says another section may work. An error that took the tabs away would
 *     strand the reader on the one section that cannot be shown.
 *
 * 3. **Opening is not choosing.** On the FIRST open the window walks the sections in order and
 *    shows the first one the endpoint can deliver as a PDF: a mixed document ([xml, pdf]) lands on
 *    its PDF without a click (E13a). Only a 415 moves on to the next section (this section is not
 *    a PDF); 403, 404, 5xx and network errors stop at once and show the error as before, because
 *    every further try is another DocuWare call. Only when NO section is a PDF does a second
 *    pass ask for `render=html` on the sections in order (the XML e-invoice view, E13) and show
 *    the first one that answers; if none does, the text of the first refusal is shown. A section
 *    the reader picks later is fetched alone, never skipped past: PDF first, and on 415 the HTML
 *    view of that same section.
 *
 * BLOB DISCIPLINE. The PDF is rendered from a `blob:` URL because the SWA sets a global
 * `X-Frame-Options: DENY` that refuses even same-origin framing; the CSP already allows
 * `frame-src blob:` (web/public/staticwebapp.config.json, unchanged here). The URL is revoked on
 * every section change and on unmount — see the effect — and a section that is still loading
 * when the reader moves on is aborted, so a slow answer can never overwrite a newer one.
 *
 * XML E-INVOICE VIEW. `SectionFrame` is the single place that decides how a section's bytes are
 * shown. The HTML the endpoint returns is sanitised server-side, and is STILL only ever put on
 * screen as a `text/html` blob inside an iframe with `sandbox=""` (no allow-* token at all:
 * no scripts, opaque origin) — never through innerHTML or dangerouslySetInnerHTML into this
 * app's DOM. The same revoke/abort discipline applies. A strip above the frame says it is the
 * electronic invoice (XML) in the AdE stylesheet and not a scanned or PDF original.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, AlertTriangle, Download, FileText, FileCode } from 'lucide-react';
import { DocuWareDocLink } from './DocuWareDocLink';
import { useDialog } from '../ui/useDialog';
import {
    docuwareInvoicesApi, DocuWarePdfError, type DocuWareSectionContent,
} from '../../services/docuwareInvoicesApi';
import { companyLabel, type DocuWareInvoiceRow } from '../../types/docuwareInvoices';
import { formatDate } from '../../utils/format';

interface Props {
    row: DocuWareInvoiceRow;
    /** From `docuWareSectionCount(row)`; the table only opens this window for 1 or more. */
    sectionCount: number;
    onCopyDocId: (docId: string) => void;
    onClose: () => void;
}

type SectionState =
    | { status: 'loading' }
    | { status: 'ready'; url: string; contentType: DocuWareSectionContent['contentType'] }
    | { status: 'error'; message: string; httpStatus: number; allTried: boolean };

/** What the effect should fetch. A new object per request, so picking the same section again retries. */
interface SectionRequest {
    section: number;
    /** First open only: on 415 try the next section instead of stopping. */
    probe: boolean;
}

/** Sections are buttons up to here; a filing accident with more gets a select instead. */
const MAX_SECTION_BUTTONS = 30;

function formatAmount(amount: number | null, currency: string | null): string | null {
    if (amount === null || amount === undefined) return null;
    const n = amount.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    // A missing currency is shown as «?», never dropped and never guessed (masterplan §2 rule 3).
    return `${n} ${currency || '?'}`;
}

/** How one section's bytes are put on screen. The only place that knows the content type. */
function SectionFrame({ url, contentType, title }: {
    url: string;
    contentType: DocuWareSectionContent['contentType'];
    title: string;
}) {
    if (contentType === 'text/html') {
        // sandbox="" on purpose: not one allow-* token. The document runs without scripts, forms,
        // popups or same-origin access, whatever the server-side sanitiser let through.
        return (
            <iframe
                key={url}
                src={url}
                title={title}
                sandbox=""
                referrerPolicy="no-referrer"
                className="h-full w-full rounded border border-gray-300 bg-white"
            />
        );
    }
    return (
        <iframe
            key={url}
            src={url}
            title={title}
            className="h-full w-full rounded border border-gray-300 bg-white"
        />
    );
}

/** A section that is not a PDF: the endpoint's 415. The only status that lets the window move on. */
function isNotPdf(err: unknown): err is DocuWarePdfError {
    return err instanceof DocuWarePdfError && err.status === 415;
}

export function DocuWarePdfViewerModal({ row, sectionCount, onCopyDocId, onClose }: Props) {
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    // `section` is the one the section bar marks: the one on screen, or the one that failed. It
    // follows the answer, not the attempt, so the bar never marks a section that is only probed.
    const [section, setSection] = useState(1);
    const [request, setRequest] = useState<SectionRequest>({ section: 1, probe: true });
    const [state, setState] = useState<SectionState>({ status: 'loading' });

    // Panel takes the first focus, Tab stays inside, Esc closes, focus goes back to the opener.
    useDialog(panelRef, onClose);

    function pickSection(n: number) {
        setSection(n);
        setRequest({ section: n, probe: false });
    }

    useEffect(() => {
        const controller = new AbortController();
        const { signal } = controller;
        let created: string | null = null;
        setState({ status: 'loading' });

        const show = ({ blob, contentType }: DocuWareSectionContent, n: number) => {
            created = URL.createObjectURL(blob);
            setSection(n);
            setState({ status: 'ready', url: created, contentType });
        };
        const fail = (err: unknown, n: number, allTried = false) => {
            setSection(n);
            setState({
                status: 'error',
                message: err instanceof Error ? err.message : 'The document could not be opened.',
                httpStatus: err instanceof DocuWarePdfError ? err.status : 0,
                allTried,
            });
        };

        void (async () => {
            const first = request.section;
            // The first 415 of the PDF pass: the sentence to show if even the HTML pass has none.
            let firstRefusal: DocuWarePdfError | null = null;

            // Pass 1, PDF only. A probe walks every section, a pick fetches its one section.
            const last = request.probe ? sectionCount : first;
            for (let n = first; n <= last; n++) {
                try {
                    const content = await docuwareInvoicesApi.fetchSection(row.id, n, signal);
                    if (signal.aborted) return;
                    show(content, n);
                    return;
                } catch (err) {
                    if (signal.aborted) return;
                    // Only a 415 keeps going; anything else is shown now, as before.
                    if (!isNotPdf(err)) { fail(err, n); return; }
                    firstRefusal ??= err;
                }
            }

            // Pass 2, only because no section was a PDF: the XML e-invoice view. A probe tries the
            // sections in order, a pick tries its own section. A 415 here (e.g. FSM10, .msg) moves on;
            // 413/422/5xx are facts about that section and are shown at once.
            let firstHtmlRefusal: DocuWarePdfError | null = null;
            for (let n = first; n <= last; n++) {
                try {
                    const content = await docuwareInvoicesApi.fetchSection(row.id, n, signal, 'html');
                    if (signal.aborted) return;
                    show(content, n);
                    return;
                } catch (err) {
                    if (signal.aborted) return;
                    if (!isNotPdf(err)) { fail(err, n); return; }
                    firstHtmlRefusal ??= err;
                }
            }

            // Nothing could be shown: the first section's own sentence, bar back on that section.
            const refusal = firstHtmlRefusal ?? firstRefusal;
            if (!refusal) return;
            setSection(first);
            setState({
                status: 'error',
                message: refusal.message,
                httpStatus: 415,
                allTried: request.probe && sectionCount > 1,
            });
        })();

        // Revoked on every change AND on unmount. Revoking right after assigning it to the
        // iframe instead would race the browser's own PDF plugin and show an empty frame.
        return () => {
            controller.abort();
            if (created) URL.revokeObjectURL(created);
        };
    }, [row.id, request, sectionCount]);

    const sections = Array.from({ length: Math.min(sectionCount, MAX_SECTION_BUTTONS) }, (_, i) => i + 1);
    const manySections = sectionCount > MAX_SECTION_BUTTONS;
    const amount = formatAmount(row.amount, row.currency);
    const fileName = `${row.companyCode}_${row.invoiceNumber || row.docId || row.id}_s${section}.pdf`
        .replace(/[^a-zA-Z0-9_.-]/g, '_');
    const subtitle = [
        companyLabel(row.companyCode),
        row.invoiceNumber && `Invoice ${row.invoiceNumber}`,
        row.invoiceDate && formatDate(row.invoiceDate),
        amount,
    ].filter(Boolean).join(' · ');

    return createPortal(
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40"
            role="presentation"
            onClick={onClose}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                onClick={e => e.stopPropagation()}
                className="flex h-[90vh] w-[92vw] max-w-[1100px] flex-col rounded-lg bg-white shadow-xl"
            >
                <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
                    <div className="min-w-0">
                        <h3 id={titleId} className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                            <FileText aria-hidden="true" className="h-5 w-5 shrink-0 text-gray-400" />
                            <span className="truncate">{row.supplier || 'Supplier not indexed'}</span>
                        </h3>
                        <p className="mt-0.5 truncate text-sm text-gray-500">{subtitle}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close document"
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                        <X aria-hidden="true" className="h-5 w-5" />
                    </button>
                </div>

                {sectionCount > 1 && (
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-2 border-b border-gray-200 px-6 py-2">
                        {/* Numbered, never named. Section order carries no meaning. */}
                        <span
                            id={`${titleId}-sections`}
                            className="text-xs text-gray-500"
                            title="A document is a staple of sections; their order carries no meaning."
                        >
                            Section
                        </span>
                        {manySections ? (
                            <select
                                aria-labelledby={`${titleId}-sections`}
                                value={section}
                                onChange={e => pickSection(Number(e.target.value))}
                                className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            >
                                {Array.from({ length: sectionCount }, (_, i) => i + 1).map(n => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        ) : (
                            <div role="group" aria-labelledby={`${titleId}-sections`} className="flex flex-wrap items-center gap-1">
                                {sections.map(n => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => pickSection(n)}
                                        aria-pressed={n === section}
                                        aria-label={`Section ${n}`}
                                        className={`inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md px-2 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${n === section
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                        )}
                        <span className="text-xs text-gray-500">of {sectionCount}</span>
                    </div>
                )}

                {state.status === 'ready' && state.contentType === 'text/html' && (
                    <p
                        role="note"
                        className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-6 py-2 text-xs text-amber-900"
                    >
                        <FileCode aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>
                            Electronic invoice (XML), shown with the Italian tax authority stylesheet (AdE).
                            This is not a PDF original; section {section} of this document holds the XML file.
                        </span>
                    </p>
                )}

                <div className="flex-1 overflow-hidden bg-gray-100 p-3">
                    {state.status === 'loading' && (
                        <div role="status" className="flex h-full items-center justify-center">
                            <Loader2 aria-hidden="true" className="h-6 w-6 animate-spin text-blue-500 motion-reduce:animate-none" />
                            <span className="ml-2 text-gray-500">Loading document…</span>
                        </div>
                    )}
                    {state.status === 'error' && (
                        <div className="flex h-full flex-col items-center justify-center px-8 text-center">
                            {/* 415 is "this section is not a PDF", a fact about the section, so amber
                                (someone may want another section); everything else is a failure. */}
                            <AlertTriangle
                                aria-hidden="true"
                                className={`h-8 w-8 ${state.httpStatus === 415 ? 'text-amber-500' : 'text-red-600'}`}
                            />
                            {/* The endpoint's own sentence, verbatim: it knows why. */}
                            <p role="alert" className="mt-3 max-w-lg text-sm text-gray-700">{state.message}</p>
                            {state.httpStatus === 415 && sectionCount > 1 && (
                                <p className="mt-2 max-w-lg text-sm text-gray-600">
                                    {state.allTried
                                        ? `None of the ${sectionCount} sections of this document could be shown here.`
                                        : `This document has ${sectionCount} sections. Another section may be shown here.`}
                                </p>
                            )}
                            {row.documentUrl && (
                                <a
                                    href={row.documentUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-3 text-sm text-blue-600 hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                >
                                    {row.documentUrlVerified ? 'Open in DocuWare instead' : 'Open in DocuWare instead (link format not confirmed)'}
                                </a>
                            )}
                        </div>
                    )}
                    {state.status === 'ready' && (
                        <SectionFrame
                            url={state.url}
                            contentType={state.contentType}
                            title={state.contentType === 'text/html'
                                ? `Electronic invoice (XML view), section ${section}`
                                : fileName}
                        />
                    )}
                </div>

                <div className="flex items-center justify-between gap-4 border-t border-gray-200 px-6 py-3">
                    <DocuWareDocLink row={row} onCopyDocId={onCopyDocId} variant="inline" />
                    {/* Download is for the PDF only: the XML view is a rendering, not the archived file. */}
                    {state.status === 'ready' && state.contentType === 'application/pdf' && (
                        <a
                            href={state.url}
                            download={fileName}
                            className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                        >
                            <Download aria-hidden="true" className="h-4 w-4" />
                            Download
                        </a>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
